import { convertDateToLocaleString, loadWrapper, PauseCover, useUnsavedChanges } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { TlfContext } from "contexts/TlfContext";
import { IRequestStatus } from "functions/api/interface";
import { NotificationViewDto } from "functions/api/notifications/getUserNotifications";
import { NotificationQueryParams } from "functions/api/notifications/updateAllNotificationsAsViewed";
import { updateNotificationStatus } from "functions/api/notifications/updateNotificationStatus";
import { Dispatch, SetStateAction, useContext, useEffect, useState, useTransition } from "react";
import { ListGroup } from "react-bootstrap";
import { NotificationListItemEnvelope } from "./NotificationListItemEnvelope";

interface NotificationListItemProps {
  notification: NotificationViewDto;
  setShowNotifications: Dispatch<SetStateAction<boolean>>;
  setNotificationsList: Dispatch<SetStateAction<NotificationViewDto[]>>;
}

/**
 * Update a single notification within the list by matching `notificationId`.
 *
 * @param prev - Previous array of notifications
 * @param notification - Notification object containing the updated view state
 */
export const updateNotificationList = (
  prev: NotificationViewDto[],
  notification: NotificationViewDto,
) =>
  prev.map((n) =>
    n.notificationId === notification.notificationId
      ? {
          ...n,
          notificationViewed: notification.notificationViewed,
        }
      : n,
  );

/**
 * Notification list item component which renders a single notification and
 * exposes controls to toggle viewed state and navigate to its context.
 *
 * @param props - Destructured parameter object
 * @param props.notification - Notification data for this list item
 * @param props.setNotificationsList - Callback to update the notifications list
 * @param props.setShowNotifications - Callback to show/hide the notifications panel
 */
export const NotificationListItem = ({
  notification: n,
  setNotificationsList,
  setShowNotifications,
}: NotificationListItemProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const { navigate, isLoading } = useUnsavedChanges("NotificationListItem");
  const [doNavigate, setDoNavigate] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const [updateNotificationStatusRequest, setUpdateNotificationStatusRequest] = useState<
    IRequestStatus<NotificationQueryParams>
  >({
    requesting: false,
    error: false,
  });
  /**
   * Toggle the viewed status of a notification.
   *
   * @param notificationId - ID of the notification to update
   */
  const toggleNotificationStatus = async (notificationId: number) => {
    const response = await loadWrapper(
      updateNotificationStatus,
      { notificationId },
      { ...updateNotificationStatusRequest, requestedId: undefined },
      setUpdateNotificationStatusRequest,
      appSettings.backEndUrl,
    );

    /* istanbul ignore else */
    if (response?.success) {
      setNotificationsList((prev) => updateNotificationList(prev, response.data));
    }
  };

  // Close notifications after navigating
  useEffect(() => {
    if (doNavigate && isLoading && !isNavigating) {
      startTransition(() => {
        setIsNavigating(true);
        setDoNavigate(false);
      });
    } else if (isNavigating && !isLoading) {
      startTransition(() => {
        setShowNotifications(false);
        setIsNavigating(false);
      });
    }
  }, [doNavigate, isLoading, isNavigating, setShowNotifications]);

  return (
    <ListGroup
      id={`notification-${n.notificationId}`}
      horizontal
    >
      <ListGroup.Item
        action
        active={false}
        title={
          n.commentId
            ? "Click to View the associated Comment"
            : "Associated Comment has been deleted"
        }
        id={`notification-${n.notificationId}-content`}
        className="d-flex justify-content-between align-items-start"
        style={{ cursor: "pointer" }}
        variant={n.notificationViewed ? "light" : "primary"}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          /* istanbul ignore else */
          if (!n.notificationViewed) toggleNotificationStatus(n.notificationId);
          const commentId = n.commentId ? `&commentId=${n.commentId}` : "";
          const target =
            n.entityType === "Hierarchy"
              ? {
                  pathname: `/H/${n.entityId}`,
                  search: `showHierarchyComments=true${commentId}`,
                }
              : {
                  pathname: `/TlfDesigner/${n.entityId}`,
                  search: `showTlfItemComments=true${commentId}`,
                };
          /* istanbul ignore else */
          if (
            (n.entityType === "Hierarchy" && n.entityId !== hierarchyContext?.hierarchy?.id) ||
            (n.entityType === "TlfItem" && n.entityId !== tlfContext?.tlfData?.id)
          )
            setDoNavigate(true);
          else {
            setShowNotifications(false);
          }
          navigate(target);
        }}
      >
        {(doNavigate || isNavigating) && <PauseCover text="Loading comment..." />}
        <div className="ms-2 me-auto">
          <div className={n.notificationViewed ? "fw-normal" : "fw-bold"}>
            {n.notificationContent}
          </div>
          {!n.commentId && (
            <div>
              <small className="text-info">(Comment has been deleted)</small>
            </div>
          )}
          <small className="text-muted">
            {convertDateToLocaleString(n.notificationDateTimeStamp)}
          </small>
        </div>
      </ListGroup.Item>
      <NotificationListItemEnvelope
        id={n.notificationId}
        viewed={n.notificationViewed}
        toggleNotificationStatus={toggleNotificationStatus}
      />
    </ListGroup>
  );
};

NotificationListItem.displayName = "NotificationListItem";
